import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  getCampaignDashboard,
  getTransferDurations,
  resetCampaignList,
  campaignActionErrorMessage,
  type CampaignDashboard as CampaignDashboardData,
  type CampaignTransferDurations,
} from "../api/campaigns";
import { withFees, FEES_NOTE } from "../lib/fees";

// Order matches the backend "buckets" array. "Connected" = transfer reached an
// agent but talk time wasn't recorded; only "<10s" is a genuinely brief call.
const DURATION_BUCKETS: [string, keyof CampaignTransferDurations][] = [
  ["Connected", "b_connected_nodur"],
  ["<10s", "b_under_10s"],
  ["10s–2m", "b_10s_2m"],
  ["2–5m", "b_2m_5m"],
  ["5–10m", "b_5m_10m"],
  ["10–15m", "b_10m_15m"],
  ["15–20m", "b_15m_20m"],
  ["20m+", "b_over_20m"],
];

function TransferDurations({ campaignId, window }: { campaignId: number; window: string }) {
  const { data } = useQuery({
    queryKey: ["transfer-durations", window],
    queryFn: () => getTransferDurations(window),
    refetchInterval: 30000,
  });
  const d = data?.campaigns.find((c) => c.campaign_id === campaignId);
  const total = d?.transfers ?? 0;
  // Every transfer connected (a press only happens on an answered call). The only
  // ones that aren't a real reached-agent call are the genuinely brief <10s ones.
  const connectedPct = total > 0 ? Math.round(((total - (d?.b_under_10s ?? 0)) / total) * 100) : 0;
  return (
    <div className="bg-white rounded-lg shadow p-4 mt-4">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <h3 className="text-sm font-semibold">Transfer call durations</h3>
        {total > 0 && (
          <span className="text-xs text-gray-500">
            {total.toLocaleString()} transfers ·{" "}
            <span className="text-green-700 font-semibold">{connectedPct}% reached an agent</span>
          </span>
        )}
      </div>
      {total === 0 ? (
        <p className="text-gray-400 text-sm">No transfers in this window.</p>
      ) : (
        <div className="space-y-1.5">
          {DURATION_BUCKETS.map(([label, key]) => {
            const val = (d?.[key] as number) ?? 0;
            const pct = total > 0 ? (val / total) * 100 : 0;
            const brief = key === "b_under_10s";
            return (
              <div key={label} className="flex items-center gap-2 text-sm">
                <div className="w-16 text-gray-600">{label}</div>
                <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                  <div className={`h-full ${brief ? "bg-amber-400" : "bg-brand"}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="w-12 text-right font-mono text-gray-700">{val.toLocaleString()}</div>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-2">
        Every transfer reached an agent. <b>Connected</b> = talk time not recorded (a later
        redial overwrote the call log); <span className="text-amber-600">&lt;10s</span> = genuinely brief calls.
      </p>
    </div>
  );
}

function LeadProgress({
  p,
  onReset,
  resetPending,
}: {
  p: CampaignDashboardData["progress"];
  onReset?: () => void;
  resetPending?: boolean;
}) {
  const dialable = Math.max(p.total - p.dnc, 0);
  const pct = dialable > 0 ? Math.round((p.dialed / dialable) * 100) : 0;
  const canReset = p.active === 0 && (p.completed > 0 || p.failed > 0 || p.pending_retry > 0);
  return (
    <div className="bg-white rounded-lg shadow p-4 mb-4">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-gray-700">Lead progress</h3>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{p.dialed.toLocaleString()}</span> dialed of{" "}
            {p.total.toLocaleString()} ·{" "}
            <span className="font-semibold text-gray-900">{p.remaining.toLocaleString()}</span> remaining
            <span className="text-gray-400"> ({pct}%)</span>
          </span>
          {onReset && (
            <button
              type="button"
              disabled={!canReset || resetPending}
              title={
                p.active > 0
                  ? "Stop the campaign and let active calls finish before resetting"
                  : "Reset dialed contacts to pending (DNC numbers stay suppressed)"
              }
              onClick={onReset}
              className="px-2.5 py-1 rounded border border-gray-300 bg-gray-50 hover:bg-gray-100 text-xs font-medium text-gray-700 disabled:opacity-40"
            >
              {resetPending ? "Resetting…" : "Reset list"}
            </button>
          )}
        </div>
      </div>
      <div className="h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
        <span>Not yet dialed: <b className="text-gray-700">{p.pending.toLocaleString()}</b></span>
        <span>Awaiting retry: <b className="text-gray-700">{p.pending_retry.toLocaleString()}</b></span>
        <span>In progress: <b className="text-gray-700">{p.active.toLocaleString()}</b></span>
        <span>Completed: <b className="text-gray-700">{p.completed.toLocaleString()}</b></span>
        <span>Failed: <b className="text-gray-700">{p.failed.toLocaleString()}</b></span>
        {p.dnc > 0 && <span>Removed (DNC): <b className="text-gray-700">{p.dnc.toLocaleString()}</b></span>}
      </div>
    </div>
  );
}

const DISPO_COLORS: Record<string, string> = {
  ANSWERED: "#16a34a",
  "NO ANSWER": "#9ca3af",
  BUSY: "#f59e0b",
  FAILED: "#dc2626",
  CONGESTION: "#ea580c",
  unknown: "#6b7280",
};
const PALETTE = ["#2563eb", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed", "#0891b2", "#9ca3af", "#ea580c"];

function fmtHour(iso: string): string {
  const d = new Date(iso);
  return `${d.getHours()}:00`;
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-lg shadow px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

const WINDOWS = [
  { key: "today", label: "Today" },
  { key: "24h", label: "Last 24h" },
  { key: "all", label: "All time" },
];

export function CampaignDashboard({ campaignId, name }: { campaignId: number; name: string }) {
  const qc = useQueryClient();
  const [window, setWindow] = useState("24h");
  const [resetMsg, setResetMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["campaign-dashboard", campaignId, window],
    queryFn: () => getCampaignDashboard(campaignId, window),
    refetchInterval: 15000, // live refresh
  });
  const resetMut = useMutation({
    mutationFn: () => resetCampaignList(campaignId),
    onSuccess: (res) => {
      const dncNote = res.dnc_suppressed > 0 ? ` (${res.dnc_suppressed.toLocaleString()} newly DNC-suppressed)` : "";
      setResetMsg({
        ok: true,
        text: `Reset ${res.reset.toLocaleString()} contacts to pending (${res.pending_total.toLocaleString()} ready to dial)${dncNote}.`,
      });
      qc.invalidateQueries({ queryKey: ["campaign-dashboard", campaignId] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (err) => {
      setResetMsg({ ok: false, text: campaignActionErrorMessage(err) });
    },
  });

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">
          Live dashboard — <span className="text-brand">{name}</span>
        </h2>
        <div className="flex gap-1 text-sm">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              onClick={() => setWindow(w.key)}
              className={`px-3 py-1 rounded ${window === w.key ? "bg-brand text-white" : "bg-gray-100 hover:bg-gray-200"}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading || !data ? (
        <p className="text-gray-500 text-sm">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 mb-4">
            <Kpi label="Dialed" value={data.kpis.total_dialed.toLocaleString()} />
            <Kpi label="Connected" value={data.kpis.connected.toLocaleString()} sub={`${data.kpis.connect_rate}%`} />
            <Kpi label="Transfers" value={data.kpis.transfers.toLocaleString()} sub={`${data.kpis.transfer_rate}% of conn.`} />
            <Kpi label="Avg talk" value={`${data.kpis.avg_duration_sec}s`} />
            <Kpi label="Active ch." value={data.kpis.active_channels.toLocaleString()} sub={`${data.kpis.cps} cps`} />
            <Kpi label="Cost" value={`$${withFees(data.kpis.total_cost).toFixed(2)}`} sub={FEES_NOTE} />
            <Kpi label="Cost/xfer" value={`$${withFees(data.kpis.cost_per_transfer).toFixed(3)}`} />
            <Kpi label="Credit" value={`$${data.kpis.credit_remaining.toFixed(2)}`} />
          </div>

          {resetMsg && (
            <div
              role={resetMsg.ok ? "status" : "alert"}
              className={`mb-3 rounded border px-3 py-2 text-xs ${
                resetMsg.ok
                  ? "border-green-300 bg-green-50 text-green-800"
                  : "border-red-300 bg-red-50 text-red-700"
              }`}
            >
              {resetMsg.text}
            </div>
          )}

          <LeadProgress
            p={data.progress}
            resetPending={resetMut.isPending}
            onReset={() => {
              if (
                confirm(
                  `Reset dial list for "${name}"? Dialed and failed contacts will be queued again while DNC numbers stay suppressed.`,
                )
              ) {
                resetMut.mutate();
              }
            }}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow p-4 lg:col-span-2">
              <h3 className="text-sm font-semibold mb-2">Calls per hour</h3>
              {data.hourly.length === 0 ? (
                <p className="text-gray-400 text-sm">No calls in this window.</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={data.hourly} margin={{ left: -10, right: 8, top: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="hour" tickFormatter={fmtHour} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip labelFormatter={(v) => new Date(v as string).toLocaleString()} />
                    <Legend />
                    <Area type="monotone" dataKey="dialed" name="Dialed" stroke="#2563eb" fill="#dbeafe" />
                    <Area type="monotone" dataKey="connected" name="Connected" stroke="#16a34a" fill="#dcfce7" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-semibold mb-2">Dispositions</h3>
              {data.dispositions.length === 0 ? (
                <p className="text-gray-400 text-sm">No calls yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={data.dispositions}
                      dataKey="count"
                      nameKey="disposition"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={(e: { name?: string }) => e.name ?? ""}
                    >
                      {data.dispositions.map((d, i) => (
                        <Cell key={i} fill={DISPO_COLORS[d.disposition] ?? PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 mt-4">
            <h3 className="text-sm font-semibold mb-2">Call response breakdown</h3>
            {data.responses.length === 0 ? (
              <p className="text-gray-400 text-sm">No data.</p>
            ) : (
              <div className="space-y-1.5">
                {(() => {
                  const max = Math.max(...data.responses.map((r) => r.count), 1);
                  return data.responses.map((r) => {
                    const is603 = /603|declin/i.test(r.response);
                    return (
                      <div key={r.response} className="flex items-center gap-2 text-sm">
                        <div className="w-40 truncate text-gray-600" title={r.response}>{r.response}</div>
                        <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                          <div
                            className={`h-full ${is603 ? "bg-red-500" : "bg-brand"}`}
                            style={{ width: `${(r.count / max) * 100}%` }}
                          />
                        </div>
                        <div className="w-16 text-right font-mono text-gray-700">{r.count.toLocaleString()}</div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>

          <TransferDurations campaignId={campaignId} window={window} />
        </>
      )}
    </div>
  );
}
